/**
 * The minimized canvas chat: the canonical ChatKit FAB launcher, positioned
 * inside the React Flow shell (not viewport-fixed) so it respects the explorer /
 * right-pane layout like the rest of the canvas chat. Clicking it opens the chat
 * window. While closed it still surfaces the signal the old floating bar gave:
 * a soft rust pulse while a run is in flight.
 */
import { cn } from "@/utils";
import { ChatFab } from "@cohesive/ui-components";
import type { CSSProperties } from "react";

export function CanvasChatFab({
  onOpen,
  running,
  dockStyle,
}: {
  onOpen: () => void;
  /** A run is in progress — pulse the launcher so activity isn't missed. */
  running: boolean;
  /**
   * When set, the launcher pins to the LEFT (viewport-fixed) instead of the
   * shell's bottom-right — used while a full-width detail pane occupies the
   * right. The caller computes the geometry from the canvas content area.
   */
  dockStyle?: CSSProperties;
}) {
  return (
    <div
      data-onboarding-target="agent-chat-toggle"
      style={dockStyle}
      className={
        dockStyle
          ? "fixed z-50"
          : cn(
              "absolute bottom-4 right-4 z-30",
              // Mirror the window shell: below 400px the React Flow container is
              // too narrow, so pin to the viewport instead (matches CORNER_SHELL_CLASS).
              "@max-[399px]:fixed @max-[399px]:z-50",
            )
      }
    >
      {running && (
        // Activity pulse rendered as an expanding box-shadow ring (see the
        // `fab-ping` keyframes) rather than an `animate-ping` transform. A
        // scaled overlay bleeds past this corner wrapper and enlarges the
        // canvas scroll area, flashing scrollbars when macOS "always show
        // scrollbars" is on; box-shadow is excluded from scrollable overflow,
        // so the pulse can grow freely without ever adding scrollbars.
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full motion-safe:animate-fab-ping"
        />
      )}
      {/* Neutralize ChatFab's own `fixed bottom-6 right-6 z-[1000]` so it sits in
          this shell-relative wrapper instead. Call `onOpen()` with no args — the
          button forwards its click event, which must not reach `expand()` (it
          would be mistaken for a session id). */}
      <ChatFab
        onClick={() => onOpen()}
        className="relative bottom-auto right-auto z-auto"
      />
    </div>
  );
}
